"use client";

import React, { useState, useEffect } from "react";
import { Loader, CircleCheck, CircleAlert } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";
const Textarea = React.forwardRef<HTMLTextAreaElement, React.TextareaHTMLAttributes<HTMLTextAreaElement>>(({
  className,
  ...props
}, ref) => {
  return <textarea className={cn("flex min-h-[80px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-base shadow-xs placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm dark:bg-input/30 transition-[color,box-shadow]", className)} ref={ref} {...props} />;
});
Textarea.displayName = "Textarea";
export default function PrintInquiry() {
  return <section id="print-inquiry" className="dark bg-background text-foreground relative py-24 px-4 md:px-8 overflow-hidden">
      {}
      <div className="absolute inset-0 z-0 opacity-20 pointer-events-none bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1556059/img/film_grain_texture.png)] bg-repeat mix-blend-overlay" />

      <div className="relative z-10 max-w-2xl mx-auto">
        <div className="text-center mb-12 space-y-4">
          <h2 className="text-3xl font-serif tracking-tight !text-[hsl(0,0%,100%)] md:text-4xl">
            Interested in a physical artifact?
          </h2>
          <p className="text-muted-foreground text-lg font-light tracking-wide">
            Select a print from the archives. Each piece is inspected for grain structure and tonal depth.
          </p>
        </div>

        <div className="bg-card/50 backdrop-blur-sm border border-border/50 rounded-xl p-6 md:p-10 shadow-2xl">
          <FormProvider formId="print_inquiry_the_archives" sectionName="print-inquiry" defaultValues={{
          name: "",
          email: "",
          phone: "",
          print_size: "",
          print_type: "Matte",
          quantity: "1",
          message: ""
        }} validationRules={{
          name: {
            required: true
          },
          email: {
            required: true,
            pattern: "email"
          },
          print_size: {
            required: true
          },
          print_type: {
            required: true
          },
          quantity: {
            required: true,
            min: 1,
            max: 100
          }
        }} submitText="Send Inquiry" successMessage="Inquiry received. We will be in touch shortly with a quote." errorMessage="Unable to send inquiry. Please check your connection and try again.">
            <PrintInquiryForm />
          </FormProvider>
        </div>
      </div>
    </section>;
}
function PrintInquiryForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isSubmitting) {
      setProgress(0);
      interval = setInterval(() => {
        setProgress(prev => {
          if (prev >= 90) return prev;
          return prev + Math.random() * 10;
        });
      }, 200);
    } else if (isSubmitted) {
      setProgress(100);
    } else {
      setProgress(0);
    }
    return () => clearInterval(interval);
  }, [isSubmitting, isSubmitted]);
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-6 animate-in fade-in zoom-in duration-500">
        <div className="size-16 rounded-full bg-primary/20 flex items-center justify-center text-primary mb-2">
          <CircleCheck className="size-8" />
        </div>
        <h3 className="text-2xl font-serif text-foreground">Inquiry Sent</h3>
        <p className="text-muted-foreground max-w-md">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-4 border-primary/50 hover:bg-primary/10 hover:text-primary">
          Request Another Print
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {}
        <FormField control={control} name="name" rules={{
        required: "Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Your Name</FormLabel>
              <FormControl>
                <Input placeholder="Jane Doe" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="jane@example.com" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {}
        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel>Phone Number <span className="text-muted-foreground text-xs font-normal ml-1">(Optional)</span></FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+1 555-123-4567" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="quantity" rules={{
        required: "Quantity is required",
        min: {
          value: 1,
          message: "Minimum 1"
        },
        max: {
          value: 100,
          message: "Maximum 100"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Quantity</FormLabel>
              <FormControl>
                <Input type="number" min={1} max={100} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      {}
      <FormField control={control} name="print_size" rules={{
      required: "Please select a size"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel>Print Size</FormLabel>
            <Select onValueChange={field.onChange} defaultValue={field.value}>
              <FormControl>
                <SelectTrigger>
                  <SelectValue placeholder="Select a size" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                <SelectItem value='8" x 10"'>8" x 10"</SelectItem>
                <SelectItem value='11" x 14"'>11" x 14"</SelectItem>
                <SelectItem value='16" x 20"'>16" x 20"</SelectItem>
                <SelectItem value='20" x 30"'>20" x 30"</SelectItem>
                <SelectItem value="Custom">Custom</SelectItem>
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>} />

      {}
      <FormField control={control} name="print_type" rules={{
      required: "Please select a print type"
    }} render={({
      field
    }) => <FormItem className="space-y-3">
            <FormLabel>Print Type</FormLabel>
            <FormControl>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {["Matte", "Glossy", "Metallic", "Canvas"].map(type => <label key={type} className={cn("flex items-center justify-center gap-2 rounded-md border border-input p-3 cursor-pointer transition-all hover:bg-accent hover:text-accent-foreground", field.value === type ? "bg-primary/10 border-primary text-primary ring-1 ring-primary" : "bg-transparent")}>
                    <input type="radio" className="sr-only" name={field.name} value={type} checked={field.value === type} onChange={field.onChange} />
                    <span className="text-sm font-medium">{type}</span>
                  </label>)}
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {}
      <FormField control={control} name="message" render={({
      field
    }) => <FormItem>
            <FormLabel>Additional Details / Requests</FormLabel>
            <FormControl>
              <Textarea placeholder="Any special instructions, framing preferences, or custom size details..." className="resize-y" {...field} />
            </FormControl>
            <FormDescription>
              We'll do our best to accommodate custom requests.
            </FormDescription>
            <FormMessage />
          </FormItem>} />

      {}
      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg flex items-center gap-3 text-destructive animate-in slide-in-from-top-2">
          <CircleAlert className="size-5 shrink-0" />
          <p className="text-sm font-medium">{submitError}</p>
        </div>}

      {}
      <div className="space-y-4 pt-4">
        {isSubmitting && <div className="space-y-1.5">
            <div className="flex justify-between text-xs text-muted-foreground uppercase tracking-widest">
              <span>Scanning Request</span>
              <span>{Math.round(progress)}%</span>
            </div>
            <Progress value={progress} className="h-1" />
          </div>}
        
        <Button type="submit" disabled={isSubmitting} className="w-full h-12 text-base font-medium tracking-wide" size="lg">
          {isSubmitting ? <>
              <Loader className="mr-2 size-4 animate-spin" />
              Processing...
            </> : "Send Inquiry"}
        </Button>
      </div>
    </Form>;
}